import React, { useState, useMemo, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form as FormikForm, useField, useFormikContext } from "formik";
import { Modal, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import {
  Building2,
  User,
  Hash,
  Globe,
  Instagram,
  MapPin,
  ImagePlus,
  Images,
  X,
  Mail,
  Phone,
  PackageCheck,
  UploadCloud,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { uploadImage, getPackage } from "../../services/UserServices/UserService";
import { getImageUrl, extractImageUrl } from "../../utils/imageUtils";

// Validation Regexes
const GST_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;
const INSTAGRAM_REGEX = /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._%#?&=/\-]+$/i;
const URL_REGEX = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;

const BUSINESS_TYPE_OPTIONS = [
  { label: "Wedding Planner", value: "wedding_planner" },
  { label: "Event Management Company", value: "event_management" },
  { label: "Venue / Banquet / Farmhouse", value: "venue" },
  { label: "Decorator / Production Partner", value: "decorator" },
  { label: "Invitation / Creative Agency", value: "invitation" },
  { label: "Hospitality / Travel Partner", value: "hospitality" },
  { label: "Other Wedding Service Provide", value: "other" },
];

const EMOJI_REGEX = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}]/u;
const INVALID_SPECIAL_CHARS_REGEX = /[@#$%^&*()!+={\}\[\]|\\:;"<>?/~_]/;

const validateNameCustom = (val, createError) => {
  if (!val) return true;
  const trimmed = val.trim();
  if (/^['’`]/.test(trimmed)) {
    return createError({ message: "Name cannot start with an apostrophe" });
  }
  if (/['’`]$/.test(trimmed)) {
    return createError({ message: "Name cannot end with an apostrophe" });
  }
  if (/['’`]{2,}/.test(trimmed)) {
    return createError({ message: "Name cannot contain consecutive apostrophes" });
  }
  if (/^\d+$/.test(trimmed)) {
    return createError({ message: "Name cannot be numeric-only" });
  }
  if (EMOJI_REGEX.test(trimmed)) {
    return createError({ message: "Name cannot contain emojis" });
  }
  if (INVALID_SPECIAL_CHARS_REGEX.test(trimmed)) {
    return createError({ message: "Name contains invalid special characters (e.g. @, #, $, %)" });
  }
  return true;
};

const PartnerSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(2, "Partner name must be at least 2 characters")
    .max(50, "Partner name cannot exceed 50 characters")
    .required("Partner name is required")
    .test("custom-name-validation", function (val) {
      return validateNameCustom(val, this.createError.bind(this));
    }),

  companyName: Yup.string()
    .trim()
    .min(2, "Company name must be at least 2 characters")
    .max(100, "Company name cannot exceed 100 characters")
    .required("Company name is required")
    .test("custom-company-name-validation", function (val) {
      return validateNameCustom(val, this.createError.bind(this));
    }),

 

  countryCode: Yup.string().trim().default("+91"),

  phoneNumber: Yup.string()
    .trim()
    .required("Phone number is required")
    .matches(/^\d{10}$/, "Enter a valid 10-digit phone number"),

  alternatePhoneNumber: Yup.string()
    .trim()
    .optional()
    .test("valid-alt-phone", "Enter a valid 10-digit phone number", (val) => {
      if (!val) return true;
      return /^\d{10}$/.test(val);
    }),

  businessType: Yup.string()
    .trim()
    .required("Business type is required"),
  customBusinessType: Yup.string().trim().optional(),

  city: Yup.string()
    .trim()
    .min(2, "City must be at least 2 characters")
    .max(50, "City name cannot exceed 50 characters")
    .required("City is required"),

  subscriptionId: Yup.string().trim().optional(),
  packageName: Yup.string().trim().optional(),

  gstNumber: Yup.string()
    .trim()
    .optional()
    .matches(GST_REGEX, {
      message: "Enter a valid 15-character GST number (e.g. 22AAAAA0000A1Z5)",
      excludeEmptyString: true,
    }),

  website: Yup.string()
    .trim()
    .matches(URL_REGEX, {
      message: "Enter a valid URL (e.g. https://example.com)",
      excludeEmptyString: true,
    }),

  instagram: Yup.string()
    .trim()
    .matches(INSTAGRAM_REGEX, {
      message: "Enter a valid Instagram URL (e.g. https://instagram.com/yourpage)",
      excludeEmptyString: true,
    }),

  portfolioImages: Yup.mixed()
    .optional()
    .test(
      "max-portfolio-images",
      "You cannot add or edit more than 10 portfolio images",
      (val) => !val || (Array.isArray(val) && val.length <= 10)
    ),
});

const FormField = ({ name, label, icon: Icon, placeholder, required = false, type = "text", ...rest }) => {
  const [field, meta] = useField(name);
  const { submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);

  return (
    <Form.Group className="mb-3" controlId={name}>
      <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <div className="position-relative">
        {Icon && (
          <Icon
            size={16}
            className="position-absolute text-muted"
            style={{
              left: 14,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 5,
            }}
          />
        )}
        <Form.Control
          {...field}
          type={type}
          placeholder={placeholder}
          isInvalid={isInvalid}
          style={{
            paddingLeft: Icon ? 38 : 14,
            borderRadius: "0.5rem",
            height: "44px",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
            borderColor: isInvalid ? "#dc3545" : "#E2E8F0",
          }}
          {...rest}
        />
        {isInvalid && (
          <Form.Control.Feedback type="invalid" className="d-block mt-1 small">
            {meta.error}
          </Form.Control.Feedback>
        )}
      </div>
    </Form.Group>
  );
};

const PhoneField = () => {
  const [field, meta] = useField("phoneNumber");
  const { submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);

  return (
    <Form.Group className="mb-3" controlId="phoneNumber">
      <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
        Phone Number <span className="text-danger">*</span>
      </Form.Label>
      <div className="input-group" style={{ boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)", borderRadius: "0.5rem" }}>
        <span
          className="input-group-text bg-light text-dark fw-bold border"
          style={{ borderRadius: "0.5rem 0 0 0.5rem", fontSize: "0.9rem", paddingLeft: "14px", paddingRight: "14px", borderColor: isInvalid ? "#dc3545" : "#E2E8F0" }}
        >
          +91
        </span>
        <Form.Control
          {...field}
          type="text"
          placeholder="9876543210"
          isInvalid={isInvalid}
          maxLength={10}
          style={{ borderRadius: "0 0.5rem 0.5rem 0", height: "44px", borderColor: isInvalid ? "#dc3545" : "#E2E8F0" }}
        />
        {isInvalid && (
          <Form.Control.Feedback type="invalid" className="d-block mt-1 small">
            {meta.error}
          </Form.Control.Feedback>
        )}
      </div>
    </Form.Group>
  );
};

const AlternatePhoneField = () => {
  const [field, meta] = useField("alternatePhoneNumber");
  const { submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);

  return (
    <Form.Group className="mb-3" controlId="alternatePhoneNumber">
      <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
        Alternate Phone Number <span className="text-muted text-lowercase fw-normal">(optional)</span>
      </Form.Label>
      <div className="input-group" style={{ boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)", borderRadius: "0.5rem" }}>
        <span
          className="input-group-text bg-light text-dark fw-bold border"
          style={{ borderRadius: "0.5rem 0 0 0.5rem", fontSize: "0.9rem", paddingLeft: "14px", paddingRight: "14px", borderColor: isInvalid ? "#dc3545" : "#E2E8F0" }}
        >
          +91
        </span>
        <Form.Control
          {...field}
          type="text"
          placeholder="9876543210"
          isInvalid={isInvalid}
          maxLength={10}
          style={{ borderRadius: "0 0.5rem 0.5rem 0", height: "44px", borderColor: isInvalid ? "#dc3545" : "#E2E8F0" }}
        />
        {isInvalid && (
          <Form.Control.Feedback type="invalid" className="d-block mt-1 small">
            {meta.error}
          </Form.Control.Feedback>
        )}
      </div>
    </Form.Group>
  );
};

const BusinessTypeField = () => {
  const [field, meta] = useField("businessType");
  const [customField] = useField("customBusinessType");
  const { values, submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);
  const showOtherInput = values.businessType === "other";

  return (
    <div className="mb-3">
      <Form.Group controlId="businessType" className="mb-0">
        <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
          Business Type <span className="text-danger">*</span>
        </Form.Label>
        <Form.Select
          {...field}
          isInvalid={isInvalid}
          style={{
            paddingLeft: 14,
            borderRadius: "0.5rem",
            height: "44px",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
            borderColor: isInvalid ? "#dc3545" : "#E2E8F0",
          }}
        >
          <option value="">Select Business Type</option>
          {BUSINESS_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
        {isInvalid && (
          <Form.Control.Feedback type="invalid" className="d-block mt-1 small">
            {meta.error}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      {showOtherInput && (
        <Form.Group controlId="customBusinessType" className="mt-3 mb-0">
          <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
            Mention Other <span className="text-muted text-lowercase fw-normal">(optional)</span>
          </Form.Label>
          <Form.Control
            {...customField}
            type="text"
            placeholder="Mention other business type..."
            style={{
              paddingLeft: 14,
              borderRadius: "0.5rem",
              height: "44px",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
              borderColor: "#E2E8F0",
            }}
          />
        </Form.Group>
      )}
    </div>
  );
};

const PackageSelectField = ({
  individualPackages = [],
  combinedPackages = [],
  packageType = "individual",
  setPackageType,
  required = false,
  loading = false,
}) => {
  const [field, meta] = useField("subscriptionId");
  const { setFieldValue, submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);

  const activePackages = packageType === "combined" ? combinedPackages : individualPackages;

  const handleTypeChange = (type) => {
    if (packageType === type) return;
    setPackageType(type);
    const newPackages = type === "combined" ? combinedPackages : individualPackages;
    const existsInNewList = newPackages.some(
      (pkg) => (pkg._id || pkg.id) === field.value
    );
    if (!existsInNewList) {
      setFieldValue("subscriptionId", "");
    }
  };

  return (
    <div className="mb-3">
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold text-secondary small text-uppercase mb-2 d-block">
          Select Package Type {required && <span className="text-danger">*</span>}
        </Form.Label>
        <div className="d-flex gap-2">
          <Button
            type="button"
            className="px-3 py-2 fw-semibold d-flex align-items-center gap-2"
            style={{
              borderRadius: "0.5rem",
              backgroundColor: packageType === "individual" ? "#A60000" : "#ffffff",
              borderColor: packageType === "individual" ? "#A60000" : "#E2E8F0",
              color: packageType === "individual" ? "#ffffff" : "#4a5568",
              fontSize: "0.875rem",
            }}
            onClick={() => handleTypeChange("individual")}
          >
            <PackageCheck size={16} />
            Individual Package
          </Button>
          <Button
            type="button"
            className="px-3 py-2 fw-semibold d-flex align-items-center gap-2"
            style={{
              borderRadius: "0.5rem",
              backgroundColor: packageType === "combined" ? "#A60000" : "#ffffff",
              borderColor: packageType === "combined" ? "#A60000" : "#E2E8F0",
              color: packageType === "combined" ? "#ffffff" : "#4a5568",
              fontSize: "0.875rem",
            }}
            onClick={() => handleTypeChange("combined")}
          >
            <PackageCheck size={16} />
            Combined Package
          </Button>
        </div>
      </Form.Group>

      <Form.Group controlId="subscriptionId">
        <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
          {packageType === "combined" ? "Combined Package" : "Individual Package"}{" "}
          {required && <span className="text-danger">*</span>}
        </Form.Label>
        <div className="position-relative">
          <Form.Select
            {...field}
            isInvalid={isInvalid}
            disabled={loading}
            style={{
              paddingLeft: 14,
              borderRadius: "0.5rem",
              height: "44px",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
              borderColor: isInvalid ? "#dc3545" : "#E2E8F0",
            }}
          >
            <option value="">
              {loading
                ? "Loading packages..."
                : packageType === "combined"
                ? "Select Combined Package"
                : "Select Individual Package"}
            </option>
            {activePackages && activePackages.length > 0
              ? activePackages.map((pkg) => (
                  <option key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                    {pkg.name || pkg.title} {pkg.price ? `(₹${pkg.price})` : ""}
                  </option>
                ))
              : null}
          </Form.Select>
          {isInvalid && (
            <Form.Control.Feedback type="invalid" className="d-block mt-1 small">
              {meta.error}
            </Form.Control.Feedback>
          )}
        </div>
      </Form.Group>
    </div>
  );
};

const parsePhone = (phoneStr) => {
  if (!phoneStr) return "";
  const cleaned = phoneStr.trim().replace(/\D/g, "");
  if (cleaned.length === 12 && cleaned.startsWith("91")) {
    return cleaned.slice(2);
  }
  return cleaned.slice(-10);
};

const EditPartner = ({ partner, isEditMode: isEditModeProp, onSave, onCancel }) => {
  const getInitialPortfolio = (partnerObj) => {
    if (!partnerObj) return [];
    if (Array.isArray(partnerObj.portfolioImages)) return partnerObj.portfolioImages;
    if (partnerObj.portfolioImages) return [partnerObj.portfolioImages];
    if (Array.isArray(partnerObj.portfolioImage)) return partnerObj.portfolioImage;
    if (partnerObj.portfolioImage) return [partnerObj.portfolioImage];
    if (Array.isArray(partnerObj.partnerData?.portfolioImages)) return partnerObj.partnerData.portfolioImages;
    if (Array.isArray(partnerObj.partnerData?.portfolioImage)) return partnerObj.partnerData.portfolioImage;
    if (Array.isArray(partnerObj.images)) return partnerObj.images;
    return [];
  };

  const pData = partner?.partnerData || {};

  const [logoPreview, setLogoPreview] = useState(pData.logo || partner?.logo || null);
  const [galleryPreviews, setGalleryPreviews] = useState(getInitialPortfolio(partner));
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isGalleryUploading, setIsGalleryUploading] = useState(false);
  const [packageType, setPackageType] = useState("individual");
  const [individualPackages, setIndividualPackages] = useState([]);
  const [combinedPackages, setCombinedPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoadingPackages(true);
    Promise.all([getPackage(false), getPackage(true)])
      .then(([indRes, combRes]) => {
        const indList = indRes?.data?.data || indRes?.data || [];
        const combList = combRes?.data?.data || combRes?.data || [];
        const indArr = Array.isArray(indList) ? indList : [];
        const combArr = Array.isArray(combList) ? combList : [];
        setIndividualPackages(indArr);
        setCombinedPackages(combArr);

        const subId =
          partner?.subscriptionId ||
          pData.subscriptionId ||
          partner?.packageId ||
          pData.packageId ||
          partner?.plan?._id ||
          partner?.plan?.id ||
          "";

        if (subId) {
          const isComb = combArr.some((pkg) => (pkg._id || pkg.id) === subId);
          if (isComb) {
            setPackageType("combined");
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching subscription packages:", err);
      })
      .finally(() => {
        setLoadingPackages(false);
      });
  }, [partner]);

  const isEdit =
    isEditModeProp !== undefined
      ? isEditModeProp
      : Boolean(partner && (partner._id || partner.id || partner.ownerName || partner.hostName || partner.name));

  const initialValues = useMemo(() => {
    const parsedPhone = parsePhone(partner?.phoneNumber || partner?.phone || pData.phoneNumber || pData.phone);
    const parsedAltPhone = parsePhone(partner?.alternatePhoneNumber || partner?.alternatePhone || pData.alternatePhoneNumber || pData.alternatePhone);

    const rawBusinessType = pData.businessType || partner?.businessType || "";
    const KNOWN_BUSINESS_TYPES = [
      "wedding_planner",
      "event_management",
      "venue",
      "decorator",
      "invitation",
      "hospitality",
    ];
    const isKnownBusinessType = KNOWN_BUSINESS_TYPES.includes(String(rawBusinessType).toLowerCase());
    const selectBusinessType = isKnownBusinessType
      ? String(rawBusinessType).toLowerCase()
      : (rawBusinessType ? "other" : "");
    const customBusinessType = isKnownBusinessType ? "" : rawBusinessType;

    const allPkgs = [...individualPackages, ...combinedPackages];
    const subId =
      partner?.subscriptionId ||
      pData.subscriptionId ||
      partner?.packageId ||
      pData.packageId ||
      partner?.plan?._id ||
      partner?.plan?.id ||
      (allPkgs?.find((pkg) => pkg.name === (partner?.packageName || pData.packageName || partner?.plan?.name))?._id) ||
      "";

    return {
      countryCode: partner?.countryCode || "+91",
      phoneNumber: parsedPhone,
      alternatePhoneNumber: parsedAltPhone,
      businessType: selectBusinessType,
      customBusinessType: customBusinessType,
      name: partner?.hostName || partner?.name || partner?.ownerName || "",
      companyName: pData.companyName || partner?.companyName || "",
      email: pData.email || partner?.email || "",
      city: pData.city || partner?.city || "",
      gstNumber: pData.gstNumber || partner?.gstNumber || "",
      website: pData.website || partner?.website || "",
      instagram: pData.instagram || partner?.instagram || "",
      portfolioImages: getInitialPortfolio(partner),
      subscriptionId: subId,
      packageName: partner?.packageName || pData.packageName || partner?.plan?.name || "",
      logo: pData.logo || partner?.logo || "",
    };
  }, [partner, pData, individualPackages, combinedPackages]);

  const handleLogoChange = async (e, setFieldValue) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLogoUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await uploadImage(formData);
      const url = extractImageUrl(res);

      if (url) {
        setFieldValue("logo", url);
        setLogoPreview(URL.createObjectURL(file));
        toast.success("Logo uploaded successfully!");
      } else {
        toast.error("Failed to extract uploaded logo URL");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload logo image");
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleGalleryChange = async (e, values, setFieldValue) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentList = Array.isArray(values.portfolioImages) ? values.portfolioImages : galleryPreviews;
    const currentCount = currentList.length;

    if (currentCount >= 10) {
      toast.error("Maximum 10 portfolio images allowed. You cannot add or edit more than 10 images.");
      e.target.value = "";
      return;
    }

    const availableSlots = 10 - currentCount;
    if (files.length > availableSlots) {
      toast(`Maximum limit is 10 portfolio images. Only ${availableSlots} image(s) can be added.`, { icon: "⚠️" });
    }

    const filesToUpload = files.slice(0, availableSlots);

    setIsGalleryUploading(true);
    let updatedList = [...currentList];
    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("file", file);
        const res = await uploadImage(formData);
        const url = extractImageUrl(res);

        if (url) {
          updatedList = [...updatedList, url];
          setFieldValue("portfolioImages", updatedList);
          setGalleryPreviews(updatedList);
          toast.success("Portfolio image uploaded successfully!");
        } else {
          toast.error("Failed to extract portfolio image URL");
        }
      } catch (error) {
        console.error("Error uploading gallery image:", error);
        toast.error("Failed to upload portfolio image");
      }
    }
    setIsGalleryUploading(false);
    e.target.value = "";
  };

  const removeGalleryImage = (index, values, setFieldValue) => {
    const currentList = Array.isArray(values.portfolioImages) ? values.portfolioImages : galleryPreviews;
    const updatedList = currentList.filter((_, i) => i !== index);
    setFieldValue("portfolioImages", updatedList);
    setGalleryPreviews(updatedList);
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={PartnerSchema}
      onSubmit={async (values, { setSubmitting }) => {
        const finalBusinessType =
          values.businessType === "other"
            ? (values.customBusinessType ? values.customBusinessType.trim() : "")
            : (values.businessType ? values.businessType.trim() : "");

        const responsePayload = {
          countryCode: "+91",
          phoneNumber: values.phoneNumber ? values.phoneNumber.trim() : "",
          alternatePhoneNumber: values.alternatePhoneNumber ? values.alternatePhoneNumber.trim() : "",
          businessType: finalBusinessType,
          name: values.name ? values.name.trim() : "",
          companyName: values.companyName ? values.companyName.trim() : "",
          email: values.email ? values.email.trim() : "",
          city: values.city ? values.city.trim() : "",
          gstNumber: values.gstNumber ? values.gstNumber.trim() : "",
          website: values.website ? values.website.trim() : "",
          instagram: values.instagram ? values.instagram.trim() : "",
          subscriptionId: values.subscriptionId ? values.subscriptionId.trim() : "",
          logo: values.logo || "",
          portfolioImages: Array.isArray(values.portfolioImages)
            ? values.portfolioImages
            : values.portfolioImages
            ? [values.portfolioImages]
            : [],
        };

        if (partner?._id || partner?.id) {
          responsePayload.partnerId = partner._id || partner.id;
        }

        try {
          await onSave?.(responsePayload);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting, errors }) => (
        <FormikForm noValidate>
          <Modal.Header closeButton={Boolean(onCancel)} onHide={onCancel} className="border-0 pb-0 pt-4 px-4">
            <Modal.Title className="fw-bold" style={{ color: "#A60000" }}>
              {isEdit ? "Edit Partner Details" : "Create New Partner"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="p-4 bg-white">
            {/* SECTION 1: Company Logo & Branding */}
            <div className="mb-4 pb-3 border-bottom">
              <h6
                className="text-uppercase text-secondary fw-bold mb-3"
                style={{ letterSpacing: "0.05em", fontSize: "0.8rem" }}
              >
                1. Company Logo & Branding
              </h6>
              <div className="d-flex align-items-center gap-4">
                <label htmlFor="edit-logo-upload" className="mb-0" style={{ cursor: "pointer" }}>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle border border-2 border-dashed bg-light position-relative overflow-hidden"
                    style={{
                      width: 84,
                      height: 84,
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {logoPreview ? (
                      <img
                        src={getImageUrl(logoPreview)}
                        alt="Company logo preview"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div className="text-center">
                        <ImagePlus size={26} className="text-muted mb-1" />
                        <div style={{ fontSize: "0.65rem" }} className="text-muted fw-semibold">
                          Upload
                        </div>
                      </div>
                    )}
                  </div>
                </label>
                <div>
                  <div className="fw-semibold text-dark mb-1">Company Logo</div>
                  <p className="text-muted small mb-2">{isLogoUploading ? "Uploading..." : "Upload a PNG or JPG file (Max 5MB)"}</p>
                  <label
                    htmlFor="edit-logo-upload"
                    className="btn btn-sm btn-outline-primary rounded-pill px-3 shadow-sm"
                    style={{ cursor: "pointer", fontSize: "0.8rem" }}
                  >
                    {logoPreview ? "Change Logo" : "Choose File"}
                  </label>
                  <Form.Control
                    id="edit-logo-upload"
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={(e) => handleLogoChange(e, setFieldValue)}
                  />
                </div>
              </div>
            </div>

            {/* SECTION 2: Primary Details */}
            <div className="mb-4 pb-3 border-bottom">
              <h6
                className="text-uppercase text-secondary fw-bold mb-3"
                style={{ letterSpacing: "0.05em", fontSize: "0.8rem" }}
              >
                2. Primary Details
              </h6>
              <Row>
                <Col md={6}>
                  <FormField
                    name="name"
                    label="Partner Name"
                    required
                    icon={User}
                    placeholder="e.g. Jane Doe"
                  />
                </Col>
                <Col md={6}>
                  <FormField
                    name="companyName"
                    label="Company Name"
                    required
                    icon={Building2}
                    placeholder="e.g. Acme Studios"
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormField
                    name="email"
                    label="Email Address (OPTIONAL)"
          
                    icon={Mail}
                    placeholder="e.g. partner@example.com"
                  />
                </Col>
                <Col md={6}>
                  <PhoneField />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <AlternatePhoneField />
                </Col>
              </Row>
            </div>

            {/* SECTION 3: Location & Business Info */}
            <div className="mb-4 pb-3 border-bottom">
              <h6
                className="text-uppercase text-secondary fw-bold mb-3"
                style={{ letterSpacing: "0.05em", fontSize: "0.8rem" }}
              >
                3. Location & Business Info & Package
              </h6>
              <Row>
                <Col md={12}>
                  <BusinessTypeField />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormField
                    name="city"
                    label="City"
                    required
                    icon={MapPin}
                    placeholder="e.g. Chandigarh"
                  />
                </Col>
                <Col md={6}>
                  <FormField
                    name="gstNumber"
                    label="GST Number"
                    icon={Hash}
                    placeholder="e.g. 22AAAAA0000A1Z5"
                    style={{ textTransform: "uppercase" }}
                  />
                </Col>
              </Row>

              <Row className="mt-2">
                <Col md={12}>
                  <PackageSelectField
                    individualPackages={individualPackages}
                    combinedPackages={combinedPackages}
                    packageType={packageType}
                    setPackageType={setPackageType}
                    required={false}
                    loading={loadingPackages}
                  />
                </Col>
              </Row>
            </div>

            {/* SECTION 4: Online Presence */}
            <div className="mb-4 pb-3 border-bottom">
              <h6
                className="text-uppercase text-secondary fw-bold mb-3"
                style={{ letterSpacing: "0.05em", fontSize: "0.8rem" }}
              >
                4. Online Presence
              </h6>
              <Row>
                <Col md={6}>
                  <FormField
                    name="website"
                    label="Website URL"
                    icon={Globe}
                    placeholder="https://example.com"
                  />
                </Col>
                <Col md={6}>
                  <FormField
                    name="instagram"
                    label="Instagram Link"
                    icon={Instagram}
                    placeholder="https://instagram.com/yourpage"
                  />
                </Col>
              </Row>
            </div>

            {/* SECTION 5: Portfolio Images */}
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6
                  className="text-uppercase text-secondary fw-bold mb-0"
                  style={{ letterSpacing: "0.05em", fontSize: "0.8rem" }}
                >
                  5. Portfolio Images
                </h6>
                <span className={`badge ${galleryPreviews.length >= 10 ? "bg-danger" : "bg-primary"} px-2 py-1`}>
                  {galleryPreviews.length}/10 Images
                </span>
              </div>
              <p className="text-muted small mb-3">
                You can upload up to 10 portfolio images for this partner profile. (Maximum 10 images limit)
              </p>

              <label
                htmlFor="edit-portfolio-upload"
                className={`d-flex flex-column align-items-center justify-content-center p-4 border border-2 border-dashed rounded-3 text-center mb-3 shadow-sm ${
                  galleryPreviews.length >= 10 ? "bg-light opacity-75" : "bg-light"
                }`}
                style={{ cursor: galleryPreviews.length >= 10 ? "not-allowed" : "pointer", transition: "all 0.2s ease" }}
              >
                <UploadCloud size={32} className={galleryPreviews.length >= 10 ? "text-secondary mb-2" : "text-primary mb-2"} />
                <span className="fw-semibold text-dark mb-1">Click or drag images to upload</span>
                <span className="text-muted small">
                  {isGalleryUploading
                    ? "Uploading..."
                    : galleryPreviews.length >= 10
                    ? "Maximum 10 portfolio images limit reached (You cannot edit or add more than 10 images)"
                    : "Upload portfolio images for the partner profile (Max 10 images)"}
                </span>
              </label>
              <Form.Control
                id="edit-portfolio-upload"
                type="file"
                accept="image/*"
                multiple
                disabled={galleryPreviews.length >= 10 || isGalleryUploading}
                className="d-none"
                onChange={(e) => handleGalleryChange(e, values, setFieldValue)}
              />

              {galleryPreviews.length >= 10 && (
                <small className="text-danger d-block mb-2 fw-semibold">
                  Maximum limit reached: You cannot add or edit more than 10 portfolio images.
                </small>
              )}
              {errors?.portfolioImages && (
                <small className="text-danger d-block mb-2 fw-semibold">
                  {errors.portfolioImages}
                </small>
              )}

              {galleryPreviews.length > 0 && (
                <div className="d-flex flex-wrap gap-3 mt-3">
                  {galleryPreviews.map((src, i) => (
                    <div
                      key={src + i}
                      className="position-relative rounded-3 overflow-hidden border shadow-sm"
                      style={{ width: 88, height: 88 }}
                    >
                      <img
                        src={getImageUrl(src)}
                        alt={`Portfolio preview ${i + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(i, values, setFieldValue)}
                        className="btn btn-danger btn-sm p-0 d-flex align-items-center justify-content-center position-absolute shadow"
                        style={{ top: 4, right: 4, width: 20, height: 20, borderRadius: "50%" }}
                        aria-label="Remove image"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer className="border-0 p-4 pt-0 bg-white">
            <Button
              variant="light"
              className="px-4 fw-semibold border shadow-sm"
              onClick={onCancel ? onCancel : () => navigate(-1)}
              disabled={isSubmitting}
              style={{ borderRadius: "0.5rem" }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="px-4 fw-semibold shadow-sm"
              disabled={isSubmitting}
              style={{ borderRadius: "0.5rem", backgroundcolor: "#3F3F46", borderColor: "#A60000" }}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </Modal.Footer>
        </FormikForm>
      )}
    </Formik>
  );
};

export default EditPartner;